import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";

// UPDATED: Cleared exclusion list so everyone shows up
const EXCLUDE_EMAILS = new Set([]); 

const toEmailLower = (email) => String(email || "").trim().toLowerCase();

function niceNameFromEmail(email) {
  if (!email) return "Unknown";
  const local = String(email).split("@")[0];
  return local
    .split(/[._-]/g)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

function isInfiniteValue(v) {
  if (v === "-" || v === "∞") return true;
  if (typeof v === "number" && !Number.isFinite(v)) return true;
  return false;
}

function normalizeReq(req) {
  const email = req.email || "";
  const emailLower = req.emailLower || toEmailLower(email);
  const employeeName = req.employeeName || req.name || niceNameFromEmail(email || emailLower);

  let typeRaw = String(req.type || "").trim().toLowerCase();

  // Back-compat: infer from legacy 'reason'/'notes' if type missing
  if (!typeRaw) {
    const rr = `${req.reason || ""} ${req.notes || ""}`.toLowerCase();
    if (rr.includes("working from home") || rr.includes("work from home") || rr.includes("wfh")) typeRaw = "wfh";
    else typeRaw = "vacation";
  }

  // Canonicalize types (keep in sync with AvailabilityCalendar TYPES)
  let type = typeRaw;
  if (type === "work_from_home" || type === "workfromhome" || type === "work from home" || type === "work-from-home")
    type = "wfh";
  if (type === "weekly day off") type = "weekly_day_off";

  const status = (req.status || "approved").toLowerCase();

  let kind = req.kind;
  if (!kind) {
    if (req.duration === "full") kind = "fullday";
    else if (req.duration === "am" || req.duration === "pm") kind = "time";
  }
  if (!kind) kind = req.startTime || req.start ? "time" : "fullday";

  let hours = typeof req.hours === "number" ? req.hours : null;

  // compute hours for time entries if possible
  const parseTimeToMinutes = (t) => {
    if (!t || typeof t !== "string" || !t.includes(":")) return null;
    const [hh, mm] = t.split(":").map((x) => Number(x));
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    return hh * 60 + mm;
  };
  const hoursBetween = (a, b) => {
    const s = parseTimeToMinutes(a);
    const e = parseTimeToMinutes(b);
    if (s == null || e == null || e <= s) return null;
    return (e - s) / 60;
  };

  if (kind === "time" && (hours == null || Number.isNaN(hours))) {
    const h = hoursBetween(req.startTime || req.start, req.endTime || req.end);
    hours = h == null ? 0 : h;
  }

  return { ...req, email, emailLower, employeeName, type, status, kind, hours };
}

// Usage math:
// - full day counts as 1
// - time entries count if hours >= 2, then hours/8 days
function countableHoursForType(req, typeKey) {
  const r = normalizeReq(req);
  if (r.status !== "approved") return 0;
  if (r.type !== typeKey) return 0;
  if (r.kind !== "time") return 0;
  const h = typeof r.hours === "number" ? r.hours : 0;
  return h >= 2 ? h : 0;
}

function fullDaysForType(req, typeKey) {
  const r = normalizeReq(req);
  if (r.status !== "approved") return 0;
  if (r.type !== typeKey) return 0;
  if (r.kind !== "fullday") return 0;
  return 1;
}

function computeUsageForType(requests, typeKey) {
  const fullDays = requests.reduce((sum, r) => sum + fullDaysForType(r, typeKey), 0);
  const hours = requests.reduce((sum, r) => sum + countableHoursForType(r, typeKey), 0);
  const usedDays = fullDays + hours / 8;
  return { fullDays, hours, usedDays };
}

// Vacation usage (kept as the primary report)
function computeVacationUsage(requests) {
  return computeUsageForType(requests, "vacation");
}

function computeSickUsage(requests) {
  return computeUsageForType(requests, "sick");
}

// Work From Home usage (sub-report under each employee)
function computeWfhUsage(requests) {
  return computeUsageForType(requests, "wfh");
}

function formatSmallDays(n) {
  const d = Number(n || 0);
  if (Math.abs(d - Math.round(d)) < 1e-9) return String(Math.round(d));
  return d.toFixed(1);
}

function formatAllowanceValue(v, fallback = 0) {
  if (isInfiniteValue(v)) return "∞";
  const num = Number(v ?? fallback);
  if (!Number.isFinite(num)) return "—";
  if (Math.abs(num - Math.round(num)) < 1e-9) return String(Math.round(num));
  return num.toFixed(1);
}

function formatDaysLabel(days) {
  const d = Number(days || 0);
  if (Math.abs(d - Math.round(d)) < 1e-9) return `${Math.round(d)} Days`;
  return `${d.toFixed(0)} Days`;
}

function pctBarColor(pct) {
  // Simple thresholds that visually match "how close are we to the limit"
  // <70% = green, 70–89% = amber, 90%+ = red
  if (!Number.isFinite(pct)) return "bg-gray-300";
  if (pct >= 90) return "bg-red-500";
  if (pct >= 70) return "bg-amber-500";
  return "bg-green-600";
}

function ProgressBar({ pct }) {
  if (!Number.isFinite(pct)) return null;
  const width = `${Math.max(0, Math.min(100, pct))}%`;

  return (
    <div className="mt-1">
      <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full ${pctBarColor(pct)}`} style={{ width }} />
      </div>
    </div>
  );
}

function minutesToDisplay(t) {
  if (!t || typeof t !== "string" || !t.includes(":")) return "";
  const [hhS, mmS] = t.split(":");
  let hh = Number(hhS);
  const mm = Number(mmS);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return "";
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = hh % 12;
  if (hh === 0) hh = 12;
  return `${hh}:${String(mm).padStart(2, "0")} ${ampm}`;
}

function displayTime(r) {
  const kind = r.kind || "fullday";
  if (kind === "fullday") return "All Day";
  const a = minutesToDisplay(r.startTime || r.start);
  const b = minutesToDisplay(r.endTime || r.end);
  if (a && b) return `${a}–${b}`;
  return "Time Off";
}

function entryLine(req) {
  const r = normalizeReq(req);
  const day = r.date || "";
  const t = displayTime(r);
  const type = r.type || "vacation";

  const typeLabel =
    type === "weekly_day_off"
      ? "Weekly Day Off"
      : type === "wfh"
        ? "WFH"
        : type === "sick"
        ? "Sick Day"
        : type === "meeting_with_client"
          ? "Meeting"
          : type === "education"
            ? "Education"
            : type === "industry_event"
              ? "Industry Event"
              : type === "office_closed"
                ? "Office Closed"
                : type === "closing_early"
                  ? "Closing Early"
                  : "Vacation";

  const notes = String(r.notes || "").trim();
  return `${day} • ${typeLabel} • ${t}${notes ? ` • ${notes}` : ""}`;
}

export default function Reports() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [usersRaw, setUsersRaw] = useState([]);
  const [requests, setRequests] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return [now - 1, now, now + 1, now + 2];
  }, []);

  // Load all users docs, then dedupe by emailLower (this fixes “only showing 2 people”)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        if (!alive) return;
        setUsersRaw(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Reports: failed to load users", e);
        setUsersRaw([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Load requests for year using date-range query
  useEffect(() => {
    const start = `${year}-01-01`;
    const end = `${year}-12-31`;

    const unsub = onSnapshot(
      query(collection(db, "timeOffRequests"), where("date", ">=", start), where("date", "<=", end)),
      (snap) => {
        setRequests(snap.docs.map((d) => normalizeReq({ id: d.id, ...d.data() })));
      },
      (err) => console.error("Reports: requests listener error", err)
    );

    return () => unsub();
  }, [year]);

  const roster = useMemo(() => {
    // Dedupe users by emailLower.
    // Prefer a doc that has allowances/role set. If tied, prefer the docId that looks like an email.
    const map = new Map();

    for (const u of usersRaw) {
      const email = u.email || "";
      const emailLower = u.emailLower || toEmailLower(email);

      if (!emailLower || !emailLower.includes("@")) continue;

      const candidate = {
        ...u,
        email,
        emailLower,
        name: u.name || u.displayName || niceNameFromEmail(emailLower),
        role: (u.role || "employee").toLowerCase(),
        annualAllotmentDays: u.annualAllotmentDays ?? 15,
        sickAllotmentDays: u.sickAllotmentDays ?? 10,
        workFromHomeAllotmentDays: u.workFromHomeAllotmentDays ?? 0,
      };

      const existing = map.get(emailLower);
      if (!existing) {
        map.set(emailLower, candidate);
        continue;
      }

      const existingScore =
        (existing.annualAllotmentDays != null ? 2 : 0) +
        (existing.role ? 1 : 0) +
        (String(existing.id).includes("@") ? 1 : 0);

      const candidateScore =
        (candidate.annualAllotmentDays != null ? 2 : 0) +
        (candidate.role ? 1 : 0) +
        (String(candidate.id).includes("@") ? 1 : 0);

      if (candidateScore > existingScore) map.set(emailLower, candidate);
    }

    // UPDATED: Removed logic that filters out infinite allotments or specific emails
    const filtered = [...map.values()]; 

    filtered.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return filtered;
  }, [usersRaw]);

  const requestsByEmail = useMemo(() => {
    const m = new Map();
    requests.forEach((r) => {
      const key = r.emailLower || toEmailLower(r.email || "");
      if (!key) return;
      const arr = m.get(key) || [];
      arr.push(r);
      m.set(key, arr);
    });

    // sort each employee's list by date
    for (const [k, arr] of m.entries()) {
      arr.sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
      m.set(k, arr);
    }

    return m;
  }, [requests]);

  const rows = useMemo(() => {
    return roster.map((u) => {
      const allowance = Number(u.annualAllotmentDays ?? 15);
      const wfhAllowanceRaw = u.workFromHomeAllotmentDays ?? 0;
      const wfhAllowanceNum = isInfiniteValue(wfhAllowanceRaw) ? Infinity : Number(wfhAllowanceRaw || 0);

      const sickAllowanceRaw = u.sickAllotmentDays ?? 10;
      const sickAllowanceNum = isInfiniteValue(sickAllowanceRaw) ? Infinity : Number(sickAllowanceRaw || 10);

      const reqs = requestsByEmail.get(u.emailLower) || [];
      const usage = computeVacationUsage(reqs);
      const wfhUsage = computeWfhUsage(reqs);
      const sickUsage = computeSickUsage(reqs);

      const pct = allowance > 0 ? (usage.usedDays / allowance) * 100 : 0;
      const remaining = allowance - usage.usedDays;

      // Handle infinite remaining logic
      const isInfiniteAllowance = isInfiniteValue(u.annualAllotmentDays);
      const remainingDisplay = isInfiniteAllowance ? Infinity : remaining;

      return {
        emailLower: u.emailLower,
        name: u.name,
        email: u.email || u.emailLower,
        allowance: isInfiniteAllowance ? Infinity : allowance,
        allowanceRaw: u.annualAllotmentDays, // preserve original for display
        usedDays: usage.usedDays,
        pct,
        remaining: remainingDisplay,

        wfhAllowanceRaw,
        wfhAllowanceNum,
        wfhUsedDays: wfhUsage.usedDays,
        
        sickAllowanceRaw,
        sickAllowanceNum,
        sickUsedDays: sickUsage.usedDays
      };
    }).sort((a, b) => {
      const usedPercentDifference = b.pct - a.pct;
      return usedPercentDifference || String(a.name).localeCompare(String(b.name));
    });
  }, [roster, requestsByEmail]);

  // Team allowance metrics: employees only (admins are included in roster if not infinite and not Lorraine)
  const metrics = useMemo(() => {
    // Keep the numerator and denominator on the same population. Employees with
    // unlimited vacation are visible in the table, but do not belong in the team
    // allowance calculation because they contribute no finite allotted days.
    const rowsWithFiniteAllowance = rows.filter((r) => Number.isFinite(r.allowance));
    const totalAllowance = rowsWithFiniteAllowance.reduce((s, r) => s + r.allowance, 0);
    const totalUsed = rowsWithFiniteAllowance.reduce(
      (s, r) => s + (Number.isFinite(r.usedDays) ? r.usedDays : 0),
      0
    );
    const totalRemaining = totalAllowance - totalUsed;
    const pct = totalAllowance > 0 ? (totalUsed / totalAllowance) * 100 : 0;
    return { totalAllowance, totalUsed, totalRemaining, pct };
  }, [rows]);

  return (
    <div className="p-4">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-xl font-semibold text-gray-800">Reports</div>

          <div className="flex items-center gap-2">
            <div className="text-sm text-gray-600">Year</div>
            <select
              className="border border-gray-300 rounded px-3 py-2 text-sm"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <div className="text-sm text-gray-700">
            Team Vacation Usage:{" "}
            <span className="font-semibold">{metrics.totalUsed.toFixed(1)}</span> /{" "}
            <span className="font-semibold">{metrics.totalAllowance.toFixed(0)}</span> days (
            {isFinite(metrics.pct) ? `${metrics.pct.toFixed(0)}%` : "—"})
            <span className="ml-2 text-gray-500">
              Remaining:{" "}
              <span className="font-semibold">
                {Number.isFinite(metrics.totalRemaining) ? metrics.totalRemaining.toFixed(1) : "—"}
              </span>
            </span>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left">
                <th className="py-3 px-3">Employee</th>
                <th className="py-3 px-3">Allowance</th>
                <th className="py-3 px-3">Used</th>
                <th className="py-3 px-3">Used %</th>
                <th className="py-3 px-3">Remaining</th>
                <th className="py-3 px-3">Sick Days</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r) => {
                const open = expanded === r.emailLower;
                return (
                  <React.Fragment key={r.emailLower}>
                    <tr
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpanded(open ? null : r.emailLower)}
                      title="Click to expand"
                    >
                      <td className="py-2 px-3">
                        <div className="font-medium text-gray-800">{r.name}</div>
                        <div className="text-xs text-gray-500">{r.email}</div>

                        {(r.wfhUsedDays > 0 ||
                          (Number.isFinite(r.wfhAllowanceNum) ? r.wfhAllowanceNum > 0 : r.wfhAllowanceRaw != null)) && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            WFH: {formatSmallDays(r.wfhUsedDays)} / {formatAllowanceValue(r.wfhAllowanceRaw, 0)}
                          </div>
                        )}
                      </td>

                      <td className="px-3">{formatAllowanceValue(r.allowanceRaw, 15)}</td>
                      <td className="px-3 font-semibold">{formatDaysLabel(r.usedDays)}</td>
                      <td className="px-3">
                        <div className="leading-tight">
                          <div>{isFinite(r.pct) ? `${r.pct.toFixed(0)}%` : "—"}</div>
                          {/* Progress bar is based directly on the same % used value */}
                          {isFinite(r.pct) ? <ProgressBar pct={r.pct} /> : null}
                        </div>
                      </td>
                      <td className="px-3">
                        {Number.isFinite(r.remaining) ? (
                          r.remaining < 0 ? (
                            <span className="text-red-600 font-semibold">
                              OVER BY {Math.abs(r.remaining).toFixed(0)} days
                            </span>
                          ) : (
                            `${r.remaining.toFixed(1)} days`
                          )
                        ) : (
                          "∞"
                        )}
                      </td>
                      <td className="px-3">
                        {formatSmallDays(r.sickUsedDays)} / {formatAllowanceValue(r.sickAllowanceRaw, 10)}
                      </td>
                    </tr>

                    {open && (
                      <tr className="border-b bg-gray-50">
                        <td colSpan={6} className="py-3 px-3">
                          <div className="text-xs font-semibold text-gray-700 mb-2">
                            Dates Off (this year)
                          </div>

                          {(() => {
                            const list = requestsByEmail.get(r.emailLower) || [];
                            if (list.length === 0) return <div className="text-sm text-gray-600">No entries.</div>;

                            return (
                              <div className="space-y-2">
                                {list.map((req) => (
                                  <div
                                    key={req.id}
                                    className="flex items-start justify-between gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2"
                                  >
                                    <div className="text-sm text-gray-800">{entryLine(req)}</div>
                                    <div className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                      {req.status || "approved"}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {rows.length === 0 && (
                <tr>
                  <td className="py-6 px-3 text-gray-600" colSpan={6}>
                    No employees available for reports.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
